import { Link } from "react-router-dom";
import PageBanner from "./PageBanner";
import SectionHeader from "./SectionHeader";
import React from "react";
import SocialMediaSidebar from "./SocialMediaSidebar";

const PrivacyPolicy = () => {
  return (
    <>
      <div>
        <SocialMediaSidebar />
      </div>
      <PageBanner
        title="Privacy Policy"
        breadcrumb={[
          { label: "Privacy Policy", href: "/" },
        ]}
      />

      <div className="max-w-6xl mx-auto p-4">
        <SectionHeader
          title="Privacy Policy @ AJEEVI"
          shareUrl="https://your-site.com/privacy-policy"
        />
      </div>
      
      <div className="w-full px-4 sm:px-6 md:px-10 lg:px-28 py-10 bg-white text-gray-800 text-justify">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="mb-10">
            <h1 className="text-3xl font-bold text-black mb-2">Privacy Policy</h1>
            <p className="text-lg text-black mb-4">
              <strong>Effective Date:</strong> 17-06-2025<br />
              <strong>Platform Name:</strong> SwatchhAJV<br />
              <strong>Operated by:</strong> Ajeevi Technologies Pvt. Ltd.<br />
              <strong>Location:</strong> Noida, Uttar Pradesh, India
            </p>
          </div>

          {/* Introduction Section */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              1. Introduction
            </h4>
            <p className="text-black mb-4 text-lg">
              Ajeevi Technologies Pvt. Ltd. ("Ajeevi", "we", "us", or "our") values the trust you place in us by using our services. This Privacy Policy sets forth how we collect, process, use, protect, and disclose your information when you access or use our SwatchhAJV Web Application, a digital civic platform developed to empower citizens, municipal staff, and administrators in managing Solid Waste Management (SWM) operations effectively.
            </p>
            <p className="text-black text-lg">
              SwatchhAJV is designed to improve transparency, responsiveness, and sustainability in municipal waste systems. We are committed to ensuring your personal information is handled securely, ethically, and in accordance with applicable privacy regulations, including the Digital Personal Data Protection Act, 2023.
            </p>
            <p className="text-black text-lg mt-4">
              By accessing or using SwatchhAJV, you consent to the practices described in this Privacy Policy.
            </p>
          </div>

          {/* Scope of Application */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              2. Scope of Application
            </h4>
            <p className="text-black mb-4 text-lg">
              This policy governs the processing of personal and non-personal data through the SwatchhAJV platform, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>Mobile and Web Applications</li>
              <li>APIs and integrated municipal systems</li>
              <li>Official support or helpdesk tools</li>
              <li>SMS/Email notifications and push alerts</li>
            </ul>
            <p className="text-black text-lg mb-4">
              It applies to all users, including but not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>Residents of India availing SWM services</li>
              <li>Municipal employees performing administrative or field duties</li>
              <li>Authorized contractors and operators appointed by civic bodies</li>
              <li>Other individuals interacting with the platform in any capacity</li>
            </ul>
            <p className="text-black text-lg">
              If you are accessing the platform on behalf of a third party (e.g., as a contractor or municipal staff), you represent that you have the authority to bind that entity and that you consent to this Privacy Policy on their behalf.
            </p>
          </div>

          {/* Types of Information We Collect */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              3. Types of Information We Collect
            </h4>
            <p className="text-black mb-4 text-lg">
              We may collect various types of information, either directly from you or automatically during your use of the platform.
            </p>
            
            <h5 className="text-xl font-semibold mb-3 text-black">
              3.1 Personally Identifiable Information (PII)
            </h5>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>Full Name (First Name, Last Name)</li>
              <li>Mobile Phone Number (used for OTP login and notifications)</li>
              <li>Email Address (if voluntarily provided)</li>
              <li>Aadhaar or government-issued ID (optional; collected only for verification if authorized)</li>
              <li>Home or GPS Location (with explicit user permission)</li>
              <li>Complaint Reference ID and Linked History</li>
            </ul>
            
            <h5 className="text-xl font-semibold mb-3 text-black">
              3.2 Device and Technical Metadata
            </h5>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>IP Address and Geographic Region</li>
              <li>Device Type and OS Version</li>
              <li>Browser Name, Version, and Language Preferences</li>
              <li>IMEI or Device Identifiers (only for authorized field devices)</li>
              <li>Mobile network data or Wi-Fi logs (for route validation)</li>
            </ul>
            
            <h5 className="text-xl font-semibold mb-3 text-black">
              3.3 Civic Complaint and Service Use Data
            </h5>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>Nature and Category of Complaint (garbage pickup, missed service, etc.)</li>
              <li>Media Uploads: Photos, videos, or supporting documents</li>
              <li>Date, Time, and Resolution Status of Complaints</li>
              <li>Location mapping of the complaint (either manually entered or GPS tagged)</li>
            </ul>
            
            <h5 className="text-xl font-semibold mb-3 text-black">
              3.4 Behavioral and Usage Data
            </h5>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black">
              <li>Login and logout timestamps</li>
              <li>Number of complaints registered/viewed</li>
              <li>Modules accessed (e.g., Complaint Tracker, Vehicle Route, Collection Schedule)</li>
              <li>User feedback or satisfaction rating data</li>
              <li>Search queries and platform navigation patterns</li>
            </ul>
          </div>

          {/* How We Use Your Data */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              4. How We Use Your Data
            </h4>
            <p className="text-black mb-4 text-lg">
              We collect and use your information for multiple civic and service-related purposes, including:
            </p>
            
            <h5 className="text-xl font-semibold mb-3 text-black">
              4.1 Platform Functionality
            </h5>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>To authenticate and register users securely</li>
              <li>To validate complaints and assign them to the correct civic ward or division</li>
              <li>To notify users on complaint status, route changes, or service updates</li>
              <li>To ensure accurate geolocation for field verification and service mapping</li>
            </ul>
            
            <h5 className="text-xl font-semibold mb-3 text-black">
              4.2 Municipal Service Efficiency
            </h5>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>To optimize garbage vehicle routes using live and historical data</li>
              <li>To reduce service duplication and prevent resource misuse</li>
              <li>To enable data-driven municipal planning and policy formulation</li>
              <li>To generate monthly or quarterly analytical dashboards for authorities</li>
            </ul>
            
            <h5 className="text-xl font-semibold mb-3 text-black">
              4.3 Civic Engagement
            </h5>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>To conduct surveys or feedback collection on municipal cleanliness</li>
              <li>To identify areas with recurring complaints and escalate for intervention</li>
              <li>To share educational updates or sanitation campaigns via alerts</li>
            </ul>
            
            <h5 className="text-xl font-semibold mb-3 text-black">
              4.4 Legal and Compliance
            </h5>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black">
              <li>To ensure legal compliance with Indian data protection laws</li>
              <li>To investigate misuse or abuse of the platform</li>
              <li>To comply with audit trails, government inspections, or legal summons</li>
            </ul>
          </div>

          {/* Legal Grounds for Processing */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              5. Legal Grounds for Processing (As per Indian Law)
            </h4>
            <p className="text-black mb-4 text-lg">
              We rely on the following legal bases:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black">
              <li><strong>Consent:</strong> When you voluntarily provide data or submit a complaint</li>
              <li><strong>Contractual necessity:</strong> When municipal staff access data to deliver public services</li>
              <li><strong>Public duty:</strong> To enhance sanitation, health, and governance</li>
              <li><strong>Legal obligation:</strong> Where the law mandates data retention or sharing</li>
              <li><strong>Legitimate interest:</strong> To improve municipal technology and service delivery</li>
            </ul>
          </div>

          {/* Data Sharing and Disclosure */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              6. Data Sharing and Disclosure
            </h4>
            <p className="text-black mb-4 text-lg">
              We do not sell, rent, or share your data for profit. However, we may share information under the following strictly controlled scenarios:
            </p>
            
            <h5 className="text-xl font-semibold mb-3 text-black">
              6.1 Government and Municipal Authorities
            </h5>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>For resolving submitted grievances</li>
              <li>For real-time service tracking and planning</li>
              <li>For district/state-level sanitation reports and reviews</li>
            </ul>
            
            <h5 className="text-xl font-semibold mb-3 text-black">
              6.2 Technology and Support Partners
            </h5>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>Hosting providers, SMS gateways, and map integration vendors</li>
              <li>Customer support vendors (for IVR, helpdesk, ticketing)</li>
              <li>IT security auditors and consultants (under NDAs)</li>
            </ul>
            
            <h5 className="text-xl font-semibold mb-3 text-black">
              6.3 Law Enforcement and Compliance
            </h5>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black">
              <li>Where mandated by a court order, warrant, or statute</li>
              <li>In response to legal claims, fraud, or policy violations</li>
            </ul>
            <p className="text-black text-lg mt-4">
              All third parties are bound by confidentiality agreements and data handling clauses in compliance with Indian laws.
            </p>
          </div>

          {/* Data Security and Protection */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              7. Data Security and Protection
            </h4>
            <p className="text-black mb-4 text-lg">
              We use a combination of technical, administrative, and organizational safeguards to protect your data, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>End-to-end SSL/TLS encryption of communications</li>
              <li>Role-based user access and authentication with OTPs</li>
              <li>Centralized logging and suspicious activity detection</li>
              <li>Data backups and disaster recovery protocols</li>
              <li>Server-side firewalls, IP whitelisting, and rate limiting</li>
            </ul>
            <p className="text-black text-lg mb-4">
              We continually assess risks and update our defenses. Still, no system is fully invulnerable. You are responsible for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black">
              <li>Keeping OTPs and passwords confidential</li>
              <li>Logging out of shared devices</li>
              <li>Reporting any suspicious activity to our support team</li>
            </ul>
          </div>

          {/* Data Retention Policy */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              8. Data Retention Policy
            </h4>
            <p className="text-black mb-4 text-lg">
              We follow a structured data retention and archival policy:
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 border border-gray-200">Data Type</th>
                    <th className="px-4 py-2 border border-gray-200">Retention Duration</th>
                    <th className="px-4 py-2 border border-gray-200">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-2 border border-gray-200">Complaint Records</td>
                    <td className="px-4 py-2 border border-gray-200">5 years minimum</td>
                    <td className="px-4 py-2 border border-gray-200">Government audit & traceability</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border border-gray-200">User Profiles</td>
                    <td className="px-4 py-2 border border-gray-200">Until deactivation or 2 years of inactivity</td>
                    <td className="px-4 py-2 border border-gray-200">Operational reference</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border border-gray-200">GPS/Vehicle Logs</td>
                    <td className="px-4 py-2 border border-gray-200">12 months</td>
                    <td className="px-4 py-2 border border-gray-200">Optimization & performance</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 border border-gray-200">System Logs</td>
                    <td className="px-4 py-2 border border-gray-200">90–180 days</td>
                    <td className="px-4 py-2 border border-gray-200">Security and compliance</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-black text-lg mt-4">
              Once the retention period ends, the data is securely deleted or anonymized.
            </p>
          </div>

          {/* Cookies and Digital Identifiers */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              9. Cookies and Digital Identifiers
            </h4>
            <p className="text-black mb-4 text-lg">
              SwatchhAJV uses session-based cookies and browser local storage to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black">
              <li>Maintain logged-in sessions</li>
              <li>Store location pin data (if permitted)</li>
              <li>Optimize app responsiveness</li>
            </ul>
            <p className="text-black text-lg mt-4">
              We do not use tracking cookies for advertisements, social media, or third-party analytics.
            </p>
          </div>

          {/* Your Rights as a User */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              10. Your Rights as a User
            </h4>
            <p className="text-black mb-4 text-lg">
              In accordance with Indian data protection norms, you are entitled to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li><strong>Right to Access:</strong> Obtain a copy of your stored data</li>
              <li><strong>Right to Correction:</strong> Request edits to incorrect or outdated information</li>
              <li><strong>Right to Withdraw Consent:</strong> Revoke permissions for further data processing</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your personal data (subject to legal limitations)</li>
              <li><strong>Right to Grievance Redressal:</strong> File complaints with our Data Protection Officer (DPO)</li>
            </ul>
            <p className="text-black text-lg">
              Please send an email to <a href="mailto:support@ajeevi.com" className="text-[#00D084] hover:underline">support@ajeevi.com</a> to exercise any of these rights.
            </p>
          </div>

          {/* Children's Privacy */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              11. Children's Privacy
            </h4>
            <p className="text-black text-lg">
              SwatchhAJV is not intended for children under 13. We do not knowingly collect personal data from minors. If you believe a child has used the service without parental consent, notify us immediately and we will promptly delete the associated records.
            </p>
          </div>

          {/* Third-Party Integrations */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              12. Third-Party Integrations
            </h4>
            <p className="text-black mb-4 text-lg">
              We may embed or redirect to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black">
              <li>Municipal corporation websites</li>
              <li>Government sanitation portals</li>
              <li>Google Maps or GIS-based services</li>
            </ul>
            <p className="text-black text-lg mt-4">
              We are not responsible for the privacy policies or security practices of external services. Users are advised to review such policies independently.
            </p>
          </div>

          {/* Updates to this Privacy Policy */}
          <div className="mb-10">
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              13. Updates to this Privacy Policy
            </h4>
            <p className="text-black mb-4 text-lg">
              This policy may be updated:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-lg text-black mb-4">
              <li>In response to legal or regulatory changes</li>
              <li>When we expand platform functionality</li>
              <li>Based on user feedback or risk assessments</li>
            </ul>
            <p className="text-black text-lg">
              Users will be notified via platform banner, email, or push notification. Continued use of the platform after the update constitutes acceptance of the revised terms.
            </p>
          </div>

          {/* Contact and Grievance Officer */}
          <div>
            <h4 className="text-[#00D084] text-2xl font-semibold mb-3">
              14. Contact and Grievance Officer
            </h4>
            <p className="text-black mb-4 text-lg">
              For questions, grievances, or support:
            </p>
            <p className="text-black text-lg mb-2">
              <strong>Ajeevi Technologies Pvt. Ltd.</strong><br />
              505, Advant Navis Business Park, Tower A-1<br />
              Corporate Park, Sector 142<br />
              Noida, Uttar Pradesh – 201301
            </p>
            <p className="text-black text-lg">
              📧 Email: <a href="mailto:support@ajeevi.com" className="text-[#00D084] hover:underline">support@ajeevi.com</a><br />
              📞 Phone: <a href="tel:+919654325300" className="text-[#00D084] hover:underline">+91-9654325300</a><br />
              🌐 Website: <a href="https://swachhajv.ajeevi.in" target="_blank" rel="noopener noreferrer" className="text-[#00D084] hover:underline">https://swachhajv.ajeevi.in</a>
            </p>
            <p>Note: Privacy Policy is subjected to changed anytime.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;